import React, { useContext, useState, useEffect } from 'react';
import styled, { ThemeProvider, keyframes } from 'styled-components';
import { UserSettingsProvider, UserSettingsContext } from './context/userSettings';
import { MessageProvider } from './context/messageContext';
import { TooltipProvider } from './context/tooltipContext';
import { GlobalStyle } from './styles/stylesGlobal';
import { lightTheme, darkTheme } from './styles/stylesTheme';
import { preloadCriticalAssets, preloadVideoInBackground } from './utils/assetPreloader';
import Main from './components/Main';

const themes = {
    light: lightTheme,
    dark: darkTheme,
};

// Loading screen styles
const fadeOut = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const LoadingScreen = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.gradL || '#f5f5f5'};
    z-index: 100000;
    animation: ${({ $fadeOut }) => $fadeOut ? fadeOut : 'none'} 0.3s ease-out forwards;
`;

function ThemedApp() {
    const { theme, isLoggedIn } = useContext(UserSettingsContext);
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    const [showContent, setShowContent] = useState(false);

    useEffect(() => {
        // Preload critical assets (images + fonts)
        preloadCriticalAssets().then(() => {
            setAssetsLoaded(true);
            // Small delay for fade out animation
            setTimeout(() => {
                setShowContent(true);
            }, 300);

            // Preload video in background after critical assets
            preloadVideoInBackground();
        });
    }, []);

    // Force light theme on landing page (when not logged in)
    const currentTheme = !isLoggedIn ? lightTheme : (themes[theme] || lightTheme);

    return (
        <ThemeProvider theme={currentTheme}>
            <GlobalStyle />
            {!showContent && (
                <LoadingScreen theme={currentTheme} $fadeOut={assetsLoaded} />
            )}
            {assetsLoaded && (
                <MessageProvider>
                    <TooltipProvider>
                        <Main />
                    </TooltipProvider>
                </MessageProvider>
            )}
        </ThemeProvider>
    );
}

function App() {
    return (
        <UserSettingsProvider>
            <ThemedApp />
        </UserSettingsProvider>
    );
}

export default App;
